import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { cn } from '@/utils/utils.ts';
import {
  getAvailableParameters,
  getParameterLabel,
  OPERATORS,
} from '@/features/event-rules/schemas/event-rules.utils';
import { EventRuleFormData } from '@/features/event-rules/schemas/create-event-rules.schema.ts';
import { ConditionValueBasedOnParameter } from '@/features/rules-shared/components/condition-value-based-on-parameter.tsx';

export function EditEventRuleConditionsForm({ showTitle = true }: { showTitle?: boolean }) {
  const form = useFormContext<EventRuleFormData>();
  const watchRuleType = form.watch('ruleType');
  const watchDeviceType = form.watch('deviceType');

  const { fields } = useFieldArray({
    control: form.control,
    name: 'conditions',
  });

  return (
    <div className="@container flex flex-col gap-2">
      {showTitle && (
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Conditions</h2>
        </div>
      )}

      {fields.map((field, index) => (
        <div className="group flex flex-col" key={field.id}>
          <div
            className={cn(
              'grid grid-cols-2 flex-wrap items-start gap-2 gap-y-0',
              watchRuleType === 'schedule'
                ? '@min-[800px]:grid-cols-1'
                : fields.length > 1
                  ? '@min-[800px]:grid-cols-[repeat(3,minmax(170px,_1fr))_40px]'
                  : '@min-[800px]:grid-cols-3'
            )}
          >
            <div className="flex-2">
              <FormField
                control={form.control}
                name={`conditions.${index}.fact`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="required">Parameter</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={true}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select parameter">
                            {getParameterLabel({
                              watchDeviceType,
                              watchRuleType,
                              fieldValue: field.value,
                            })}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAvailableParameters({
                          watchRuleType,
                          watchDeviceType,
                        }).map((param) => (
                          <SelectItem key={String(param.id)} value={param.id}>
                            {param.name} {param.unit && `(${param.unit})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {watchRuleType !== 'schedule' && (
              <div className="flex-2">
                <FormField
                  control={form.control}
                  name={`conditions.${index}.operator`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="required">Condition</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={true}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {OPERATORS.map((op) => (
                            <SelectItem key={String(op.value)} value={op.value}>
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {watchRuleType !== 'schedule' && (
              <div className="">
                <ConditionValueBasedOnParameter index={index} disabled={true} />
              </div>
            )}
          </div>

          <div className="relative h-4 group-last:hidden">
            <div className="absolute top-2 w-full border-b"></div>
            <div className="absolute -top-1.5 left-3/6 -translate-x-2/4">
              <span className="text-muted-foreground bg-white px-2 text-xs">AND</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
